import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plane, Calendar, Users, MapPin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import FlightBookingModal from '../../components/FlightBookingModal';

// Ajouter style responsive pour le formulaire
const formStyles = `
  @media (max-width: 1024px) {
    .quote-form-grid {
      grid-template-columns: 1fr 1fr !important;
    }
  }
  @media (max-width: 640px) {
    .quote-form-grid {
      grid-template-columns: 1fr !important;
    }
  }
`;

// Injecter les styles
if (typeof document !== 'undefined') {
  const styleId = 'quote-form-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = formStyles;
    document.head.appendChild(style);
  }
}

const Flights = () => {
  const { language } = useLanguage();
  
  // Fonction de traduction pour les services
  const translateService = (service) => {
    if (language === 'fr') return service;
    
    const translations = {
      'Boissons dont Champagne à bord': 'Beverages including Champagne on board',
      'Repas - Collations – Sandwichs – Viennoiseries...': 'Meals - Snacks - Sandwiches - Pastries...',
      'Transfert ville jusqu\'à aéroport': 'City to airport transfer',
      'WiFi haute vitesse': 'High-speed WiFi',
      'Service de conciergerie (suivant niveau carte)': 'Concierge service (depending on card level)',
      'Presse': 'Press',
      'Salons privés': 'Private lounges',
      'Parking aéroport': 'Airport parking',
      'Transfert jusqu\'à l\'aéroport': 'Transfer to airport',
      'Champagne à bord': 'Champagne on board',
      'Repas gastronomique': 'Gourmet meal',
      'Bagages supplémentaires': 'Additional baggage',
      'Presse internationale': 'International press',
      'Divertissement premium': 'Premium entertainment'
    };
    
    return translations[service] || service;
  };
  
  // Fonction de traduction pour les titres de vols
  const translateFlightTitle = (title) => {
    if (language === 'fr' || !title) return title;
    
    return title
      .replace(/VOL ALLER SIMPLE/gi, 'ONE-WAY FLIGHT')
      .replace(/VOL ALLER-RETOUR/gi, 'ROUND-TRIP FLIGHT')
      .replace(/PARIS/gi, 'PARIS')
      .replace(/LONDRES/gi, 'LONDON');
  };
  
  const [flights, setFlights] = useState([]);
  const [flightsLoading, setFlightsLoading] = useState(true);
  
  const [quoteForm, setQuoteForm] = useState({
    trip_type: 'Aller simple',
    departure: '',
    arrival: '',
    date: '',
    time: '',
    passengers: 1
  });
  
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  useEffect(() => {
    fetchFlights();
  }, []);
  
  const fetchFlights = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/flights`);
      if (response.ok) {
        const data = await response.json();
        setFlights(data);
      }
    } catch (error) {
      console.error('Error fetching flights:', error);
    } finally {
      setFlightsLoading(false);
    }
  };
  
  const handleBookingClose = (needsRefresh) => {
    setShowBookingModal(false);
    setSelectedFlight(null);
    if (needsRefresh) {
      fetchFlights(); // Refresh flights list
    }
  };
  
  const handleQuoteChange = (e) => {
    const { name, value } = e.target;
    setQuoteForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteSubmitting(true);
    setQuoteError('');
    setQuoteSuccess(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(quoteForm)
      });
      
      if (response.ok) {
        setQuoteSuccess(true);
        setQuoteForm({
          trip_type: 'Aller simple',
          departure: '',
          arrival: '',
          date: '',
          time: '',
          passengers: 1
        });
        
        setTimeout(() => {
          setQuoteSuccess(false);
        }, 5000);
      } else {
        setQuoteError(language === 'fr' ? 'Erreur lors de l\'envoi de la demande' : 'Error sending request');
      }
    } catch (error) {
      console.error('Error:', error);
      setQuoteError(language === 'fr' ? 'Erreur lors de l\'envoi de la demande' : 'Error sending request');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const mockFlights = [
    {
      departure: 'Paris (LBG)',
      arrival: 'Genève (GVA)',
      date: '9 Novembre 2025',
      time: '14:30',
      aircraft: 'Citation CJ2+',
      seats: 6,
      available: 3,
      price: '3,600',
      image: 'https://images.unsplash.com/photo-1616620418653-c2f1603f507d?w=600&q=80'
    },
    {
      departure: 'Paris (LBG)',
      arrival: 'Nice (NCE)',
      date: '18 Novembre 2025',
      time: '10:00',
      aircraft: 'Citation XLS+',
      seats: 8,
      available: 5,
      price: '3,500',
      image: 'https://images.unsplash.com/photo-1759614581731-4c7090648de0?w=600&q=80'
    },
    {
      departure: 'Frankfurt (FRA)',
      arrival: 'Paris (LBG)',
      date: '22 Décembre 2025',
      time: '16:45',
      aircraft: 'Citation XLS+',
      seats: 8,
      available: 2,
      price: '3,000',
      image: 'https://images.unsplash.com/photo-1570018577507-d6097aab1ffe?w=600&q=80'
    },
    {
      departure: 'Paris (LBG)',
      arrival: 'Monaco (MCM)',
      date: '15 Janvier 2026',
      time: '11:30',
      aircraft: 'Citation CJ2+',
      seats: 6,
      available: 4,
      price: '2,800',
      image: 'https://images.unsplash.com/photo-1616620418653-c2f1603f507d?w=600&q=80'
    },
    {
      departure: 'Paris (LBG)',
      arrival: 'Barcelone (BCN)',
      date: '28 Janvier 2026',
      time: '09:15',
      aircraft: 'Citation XLS+',
      seats: 8,
      available: 6,
      price: '2,990',
      image: 'https://images.unsplash.com/photo-1759614581731-4c7090648de0?w=600&q=80'
    },
    {
      departure: 'Londres (LTN)',
      arrival: 'Paris (LBG)',
      date: '5 Février 2026',
      time: '18:00',
      aircraft: 'Citation CJ2+',
      seats: 6,
      available: 1,
      price: '2,200',
      image: 'https://images.unsplash.com/photo-1570018577507-d6097aab1ffe?w=600&q=80'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-light)',
        padding: '32px 40px'
      }}>
        <div className="container" style={{ maxWidth: '1400px' }}>
          <Link to="/member/dashboard" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '14px'
          }}>
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
          </Link>
          <h1 className="hero-medium">
            {language === 'fr' ? 'Vols Exclusifs' : 'Exclusive Flights'}
          </h1>
          <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '8px', maxWidth: '900px', lineHeight: 1.7 }}>
            {language === 'fr' 
              ? 'Découvrez nos vols organisés et sur-mesure dans des conditions VIP exceptionnelles. Voyagez avec d\'autres membres du club pour optimiser les coûts selon notre formule tarifaire exclusive.' 
              : 'Discover our organized and tailor-made flights in exceptional VIP conditions. Travel with other club members to optimize costs according to our exclusive pricing formula.'}
          </p>
        </div>
      </div>

      {/* Quote Request Form - Format Compact */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)', paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <h2 className="heading-1" style={{ marginBottom: '24px', textAlign: 'center' }}>
            {language === 'fr' ? 'Demande de Devis Personnalisé' : 'Personalized Quote Request'}
          </h2>
          
          {quoteSuccess && (
            <div style={{
              padding: '12px 16px',
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              color: '#155724',
              marginBottom: '16px',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              {language === 'fr' 
                ? '✓ Votre demande de devis a été envoyée avec succès !' 
                : '✓ Your quote request has been successfully sent!'}
            </div>
          )}
          
          {quoteError && (
            <div style={{
              padding: '12px 16px',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              color: '#721c24',
              marginBottom: '16px',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              {quoteError}
            </div>
          )}
          
          <form onSubmit={handleQuoteSubmit} style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            padding: '24px'
          }}>
            {/* Type de vol en ligne */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { value: 'Aller simple', label: { fr: 'Aller simple', en: 'One way' } },
                { value: 'Aller-retour', label: { fr: 'Aller-retour', en: 'Round trip' } },
                { value: 'Multi-destinations', label: { fr: 'Multi-destinations', en: 'Multi-city' } }
              ].map((type) => (
                <label key={type.value} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}>
                  <input 
                    type="radio" 
                    name="trip_type" 
                    value={type.value}
                    checked={quoteForm.trip_type === type.value}
                    onChange={handleQuoteChange}
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="body-small">{type.label[language]}</span>
                </label>
              ))}
            </div>

            {/* Tous les champs sur une ligne - Style globalcharter.com */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '16px',
              flexWrap: 'wrap'
            }}
            className="quote-form-grid"
            >
              <input
                type="text"
                name="departure"
                value={quoteForm.departure}
                onChange={handleQuoteChange}
                required
                placeholder={language === 'fr' ? 'Départ' : 'Departure'}
                style={{
                  flex: '1 1 160px',
                  minWidth: '140px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
              
              <input
                type="text"
                name="arrival"
                value={quoteForm.arrival}
                onChange={handleQuoteChange}
                required
                placeholder={language === 'fr' ? 'Arrivée' : 'Arrival'}
                style={{
                  flex: '1 1 160px',
                  minWidth: '140px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
              
              <input
                type="date"
                name="date"
                value={quoteForm.date}
                onChange={handleQuoteChange}
                required
                style={{
                  flex: '1 1 150px',
                  minWidth: '130px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
              
              <input
                type="time"
                name="time"
                value={quoteForm.time}
                onChange={handleQuoteChange}
                required
                style={{
                  flex: '1 1 130px',
                  minWidth: '110px',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
              
              <select
                name="passengers"
                value={quoteForm.passengers}
                onChange={handleQuoteChange}
                required
                style={{
                  flex: '0 0 85px',
                  padding: '10px 8px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  cursor: 'pointer'
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <option key={num} value={num}>
                    {num} PAX
                  </option>
                ))}
              </select>
              
              <button 
                type="submit"
                disabled={quoteSubmitting}
                className="btn-primary" 
                style={{ 
                  flex: '0 0 105px',
                  padding: '10px 12px',
                  justifyContent: 'center',
                  opacity: quoteSubmitting ? 0.6 : 1,
                  cursor: quoteSubmitting ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: '13px'
                }}
              >
                {quoteSubmitting 
                  ? (language === 'fr' ? 'Envoi...' : 'Sending...') 
                  : (language === 'fr' ? 'Demander' : 'Request')}
              </button>
            </div>

            <p className="body-small" style={{
              textAlign: 'center',
              color: 'var(--text-secondary)',
              margin: '0'
            }}>
              {language === 'fr'
                ? 'Réponse sous 24h avec une proposition personnalisée'
                : 'Response within 24 hours with a personalized proposal'}
            </p>
          </form>
        </div>
      </section>

      {/* Flights List */}
      <section className="section-padding" style={{ paddingTop: '32px' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
            {language === 'fr' ? 'Vols Disponibles en partage de cabine' : 'Available Flights with Cabin Sharing'}
          </h2>
          
          {flightsLoading ? (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              {language === 'fr' ? 'Chargement des vols...' : 'Loading flights...'}
            </p>
          ) : flights.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}>
              <Plane size={48} style={{ color: 'var(--text-secondary)', margin: '0 auto 16px' }} />
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {language === 'fr' ? 'Aucun vol disponible pour le moment' : 'No flights available at the moment'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
              gap: '32px'
            }}>
              {flights.map((flight, index) => {
                const available = flight.total_seats - flight.seats_sold;
                const defaultImage = 'https://images.unsplash.com/photo-1616620418653-c2f1603f507d?w=600&q=80';
                // Convertir /uploads en /api/uploads pour passer par le backend
                const imageUrl = flight.thumbnail_image ? flight.thumbnail_image.replace('/uploads/', '/api/uploads/') : null;
                const flightImage = imageUrl 
                  ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`
                  : defaultImage;
                
                return (
                <div
                  key={flight.id || index}
                  className="hover-lift"
                  style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)',
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={flightImage}
                    alt={flight.title || flight.aircraft}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ padding: '24px' }}>
                    {/* Flight Title & Type Badge */}
                    {flight.title && (
                      <h3 className="heading-3" style={{ 
                        marginBottom: '8px',
                        color: 'var(--text-primary)' 
                      }}>
                        {translateFlightTitle(flight.title)}
                      </h3>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <p className="body-small" style={{
                        color: 'var(--text-light)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {flight.aircraft}
                      </p>
                      {flight.trip_type === 'Aller-retour' && (
                        <span style={{
                          padding: '4px 8px',
                          background: '#2c5f7d',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          {language === 'fr' ? 'ALLER-RETOUR' : 'ROUND TRIP'}
                        </span>
                      )}
                    </div>
                    
                    {/* Route */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <div>
                        <p className="heading-3">{flight.departure}</p>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {flight.departure_time}
                        </p>
                      </div>
                      <Plane size={20} color="var(--text-secondary)" />
                      <div>
                        <p className="heading-3">{flight.arrival}</p>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {flight.arrival_time}
                        </p>
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '12px',
                      marginBottom: '16px',
                      paddingBottom: '16px',
                      borderBottom: '1px solid var(--border-light)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={16} color="var(--text-secondary)" />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(flight.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Users size={16} color="var(--text-secondary)" />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {available}/{flight.total_seats} {language === 'fr' ? 'places' : 'seats'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', gridColumn: '1 / -1' }}>
                        <MapPin size={16} color="var(--text-secondary)" />
                        <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {language === 'fr' ? 'Durée' : 'Duration'}: {flight.duration}
                        </span>
                      </div>
                    </div>

                    {/* Prestations incluses */}
                    {flight.included_services && flight.included_services.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <p className="body-small" style={{ 
                          fontWeight: 600, 
                          marginBottom: '8px',
                          color: 'var(--text-primary)'
                        }}>
                          {language === 'fr' ? 'Prestations incluses :' : 'Included services:'}
                        </p>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0,
                          display: 'grid',
                          gap: '4px'
                        }}>
                          {flight.included_services.map((service, idx) => (
                            <li key={idx} className="body-small" style={{ 
                              color: 'var(--text-secondary)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ 
                                width: '4px', 
                                height: '4px', 
                                background: '#2c5f7d',
                                borderRadius: '50%'
                              }}></span>
                              {translateService(service)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Price & Booking */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        {flight.early_bird_price && flight.seats_sold < (flight.breakeven_seats || 0) ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{
                                textDecoration: 'line-through',
                                color: 'var(--text-secondary)',
                                fontSize: '16px'
                              }}>
                                {flight.client_price.toLocaleString('fr-FR')} €
                              </span>
                              <span style={{
                                padding: '2px 8px',
                                background: '#2c5f7d',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 600
                              }}>
                                EARLY BIRD
                              </span>
                            </div>
                            <p className="body-large" style={{ fontWeight: 600, color: '#2c5f7d' }}>
                              {flight.early_bird_price.toLocaleString('fr-FR')} €
                            </p>
                          </>
                        ) : (
                          <p className="body-large" style={{ fontWeight: 600 }}>
                            {flight.client_price.toLocaleString('fr-FR')} €
                          </p>
                        )}
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {language === 'fr' ? 'par membre' : 'per member'}
                        </p>
                      </div>
                      <button 
                        className="btn-primary" 
                        style={{ minWidth: 'auto', height: 'auto', padding: '12px 20px' }}
                        disabled={available === 0}
                        onClick={() => {
                          setSelectedFlight(flight);
                          setShowBookingModal(true);
                        }}
                      >
                        {available === 0 
                          ? (language === 'fr' ? 'Complet' : 'Full')
                          : (language === 'fr' ? 'Réserver' : 'Book')}
                      </button>
                    </div>
                </div>
              </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Flight Booking Modal */}
      {showBookingModal && selectedFlight && (
        <FlightBookingModal
          flight={selectedFlight}
          onClose={handleBookingClose}
          language={language}
        />
      )}
    </div>
  );
};

export default Flights;